import React from "react";
import { Button, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Form, FormRadioButton, FormRadioGroup, SubmitButton } from "@jsq/ds/forms";
import { stackedSm, stackedLg } from "../../../../common/themes/spacing_styles_vanilla.css";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { PositionListBaseComponent } from "../PositionList/PositionListBaseComponent";
import { ThisPositionOnly } from "../PositionList/ThisPositionOnly";
import { UpdatePositionsInAccountPositionList } from "../PositionList/UpdatePositionsInAccountPositionList";
import { UpdatePositionsWithPaymentPrefPositionList } from "../PositionList/UpdatePositionsWithPaymentPrefPositionList";
import { type ChildComponentProps } from "../types";
import { MapPositionConnectionToPositionsPaginatedType } from "../utils";
import { usePaginatedPositionsHook } from "./hooks";
import { SelectPositionsToUpdateSchema } from "./schema";

export const SelectPositionsToUpdate: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const {
    sourcePaymentPrefId,
    currentPosition,
    selectedPaymentMethod,
    setListOfPositionsToUpdateComponent,
    setAssociatePaymentPref,
  } = useConsolidatedPaymentPrefContext();
  const currentAccountId = currentPosition?.accountId;

  const {
    data: otherPositionsWithPaymentPrefAndSupportedPaymentMethod,
    loading: otherPositionsWithPaymentPrefAndSupportedPaymentMethodLoading,
    fetchMoreHandler: fetchMoreOtherPositionsWithPaymentPrefAndSupportedPaymentMethod,
  } = usePaginatedPositionsHook({
    accountId: currentAccountId ?? "",
    paymentPrefId: sourcePaymentPrefId,
    supportedPaymentMethod: selectedPaymentMethod,
    skip: !currentAccountId || !sourcePaymentPrefId,
  });

  const {
    data: otherPositionsWithPaymentPrefAndUnsupportedPaymentMethod,
    loading: otherPositionsWithPaymentPrefAndUnsupportedPaymentMethodLoading,
    fetchMoreHandler: fetchMoreOtherPositionsWithPaymentPrefAndUnsupportedPaymentMethod,
  } = usePaginatedPositionsHook({
    accountId: currentAccountId ?? "",
    paymentPrefId: sourcePaymentPrefId,
    unsupportedPaymentMethod: selectedPaymentMethod,
    skip: !currentAccountId || !sourcePaymentPrefId,
  });

  const {
    data: allPositionsInAccountAndSupportedPaymentMethod,
    loading: allPositionsInAccountAndSupportedPaymentMethodLoading,
    fetchMoreHandler: fetchMoreAllPositionsInAccountAndSupportedPaymentMethod,
  } = usePaginatedPositionsHook({
    accountId: currentAccountId ?? "",
    supportedPaymentMethod: selectedPaymentMethod,
    skip: !currentAccountId,
  });

  const {
    data: allPositionsInAccountAndUnsupportedPaymentMethod,
    loading: allPositionsInAccountAndUnsupportedPaymentMethodLoading,
    fetchMoreHandler: fetchMoreAllPositionsInAccountAndUnsupportedPaymentMethod,
  } = usePaginatedPositionsHook({
    accountId: currentAccountId ?? "",
    unsupportedPaymentMethod: selectedPaymentMethod,
    skip: !currentAccountId,
  });

  if (
    otherPositionsWithPaymentPrefAndSupportedPaymentMethodLoading ||
    otherPositionsWithPaymentPrefAndUnsupportedPaymentMethodLoading ||
    allPositionsInAccountAndSupportedPaymentMethodLoading ||
    allPositionsInAccountAndUnsupportedPaymentMethodLoading
  ) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <Text variant="h3" className={stackedLg}>
        Select positions to update
      </Text>
      {(allPositionsInAccountAndSupportedPaymentMethod?.account?.positions?.edges.length ??
        -1) > 0 && (
        <Text className={stackedLg}>
          This account has other positions. Please select which positions you would like to
          update
        </Text>
      )}
      <Form
        initialValues={{ updateTypes: "" }}
        submitOnlyChangedValues={false}
        validationSchema={SelectPositionsToUpdateSchema}
        onSubmit={(values) => {
          if (
            values.updateTypes === "otherPositionsWithPaymentPref" &&
            otherPositionsWithPaymentPrefAndSupportedPaymentMethod
          ) {
            setListOfPositionsToUpdateComponent(
              () => UpdatePositionsWithPaymentPrefPositionList,
            );
            setAssociatePaymentPref?.(
              AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref,
            );
          } else if (
            values.updateTypes === "allPositionsOnAccount" &&
            allPositionsInAccountAndSupportedPaymentMethod
          ) {
            setListOfPositionsToUpdateComponent(() => UpdatePositionsInAccountPositionList);
            setAssociatePaymentPref?.(
              AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsInAccount,
            );
          } else {
            setListOfPositionsToUpdateComponent(() => ThisPositionOnly);
            setAssociatePaymentPref?.(AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition);
          }
          onContinueClick();
        }}
      >
        <FormRadioGroup name="updateTypes">
          {otherPositionsWithPaymentPrefAndSupportedPaymentMethod?.account?.positions
            ?.edges && (
            <FormRadioButton value="otherPositionsWithPaymentPref">
              <Flex flexDirection="column">
                <div>
                  <Text className={stackedSm}>
                    All other positions with these payment instructions
                  </Text>
                  <PositionListBaseComponent
                    positionsPaginated={MapPositionConnectionToPositionsPaginatedType(
                      otherPositionsWithPaymentPrefAndSupportedPaymentMethod.account.positions,
                    )}
                    fetchMorePositions={() =>
                      void fetchMoreOtherPositionsWithPaymentPrefAndSupportedPaymentMethod()
                    }
                    positionListLoading={false}
                    positionListError={undefined}
                  />
                </div>
                {otherPositionsWithPaymentPrefAndUnsupportedPaymentMethod?.account?.positions
                  ?.edges &&
                  (otherPositionsWithPaymentPrefAndUnsupportedPaymentMethod?.account?.positions
                    ?.edges.length ?? -1) > 0 && (
                    <div>
                      <Text className="inset-sm-y">
                        The following positions cannot accept this payment instruction
                      </Text>
                      <PositionListBaseComponent
                        positionsPaginated={MapPositionConnectionToPositionsPaginatedType(
                          otherPositionsWithPaymentPrefAndUnsupportedPaymentMethod.account
                            .positions,
                        )}
                        fetchMorePositions={() =>
                          void fetchMoreOtherPositionsWithPaymentPrefAndUnsupportedPaymentMethod()
                        }
                        positionListLoading={false}
                        positionListError={undefined}
                      />
                    </div>
                  )}
              </Flex>
            </FormRadioButton>
          )}
          <FormRadioButton value="thisPositionOnly">This position only</FormRadioButton>
          {allPositionsInAccountAndSupportedPaymentMethod?.account?.positions && (
            <FormRadioButton value="allPositionsOnAccount">
              <Flex flexDirection="column">
                <Text className={stackedSm}>All positions for this account</Text>
                <PositionListBaseComponent
                  positionsPaginated={MapPositionConnectionToPositionsPaginatedType(
                    allPositionsInAccountAndSupportedPaymentMethod.account.positions,
                  )}
                  fetchMorePositions={() =>
                    void fetchMoreAllPositionsInAccountAndSupportedPaymentMethod()
                  }
                  positionListLoading={false}
                  positionListError={undefined}
                />
                {allPositionsInAccountAndUnsupportedPaymentMethod?.account?.positions?.edges &&
                  (allPositionsInAccountAndUnsupportedPaymentMethod?.account?.positions?.edges
                    .length ?? -1) > 0 && (
                    <div>
                      <Text className="inset-sm-y">
                        The following positions cannot accept this payment instruction
                      </Text>
                      <PositionListBaseComponent
                        positionsPaginated={MapPositionConnectionToPositionsPaginatedType(
                          allPositionsInAccountAndUnsupportedPaymentMethod.account.positions,
                        )}
                        fetchMorePositions={() =>
                          void fetchMoreAllPositionsInAccountAndUnsupportedPaymentMethod()
                        }
                        positionListLoading={false}
                        positionListError={undefined}
                      />
                    </div>
                  )}
              </Flex>
            </FormRadioButton>
          )}
        </FormRadioGroup>
        <Flex flexDirection="row-reverse" gap={8}>
          <SubmitButton>Continue</SubmitButton>
          <Button variant="neutral" onClick={onBackClick}>
            {onBackButtonName}
          </Button>
        </Flex>
      </Form>
    </>
  );
};
